import { buildAroValidationContext } from './aroValidationContext'
import {
  buildMaxCharRule,
  buildNoSlashRule,
  buildEmptyValueRule,
  buildNotApplicableRule,
} from './validationRuleBuilders'

/**
 * Builds the Notification Services validation context by extending the shared ARO base.
 *
 * Shared fields (recordkeeper, tpa, planSponsor, planName, planEin, planNumber,
 * planIdNumber, firstName, middleName, lastName, addressLine1, addressLine2,
 * ssn, emailAddress, IRA amounts, audience flags, and pure-validator formats:
 * ssnFormat, dobFormat, zipCodeFormat) are inherited from buildAroValidationContext.
 *
 * Notifications overrides city and zipCode to add emptyValue rules, and adds
 * state and countryCode (absent from the ARO base) plus all notification-specific fields.
 *
 * @param {string} userAudience - e.g. 'Recordkeeper', 'TPA'
 * @returns {object} Validation context for Notification Services listeners and pure validators
 */
export function buildNotificationsValidationContext(userAudience) {
  const base = buildAroValidationContext(userAudience)

  return {
    ...base,

    // Overrides: add emptyValue to fields that only carry maxChar in ARO base
    city: {
      ...base.city,
      emptyValue: buildEmptyValueRule('City'),
    },
    zipCode: {
      ...base.zipCode,
      emptyValue: buildEmptyValueRule('Zip code'),
    },

    // Fields not present in the ARO base
    state: {
      maxChar: buildMaxCharRule(22),
      emptyValue: buildEmptyValueRule('State'),
    },
    countryCode: {
      emptyValue: buildEmptyValueRule('Country code'),
    },

    // Balance fields
    vestedBalance: {
      maxChar: buildMaxCharRule(14),
    },
    totalBalance: {
      maxChar: buildMaxCharRule(14),
    },

    // Rollout request
    reason: {
      reasonRolloutReq: {
        re: /^(?:SBR|TERM)$/i,
        error:
          'Please specify if this is for a terminating plan (TERM) or small balance rollout (SBR)',
      },
    },

    // Grace period
    gracePeriod: {
      gracePeriodToRespond: {
        re: /^(?:30|35|40|45|60|90)$/,
        error:
          '30, 35, 40, 45, 60, or 90 days the participant has to respond to the rollout notice',
      },
    },

    // Rollout help company fields
    companyName: {
      noSlash: buildNoSlashRule('Company name'),
      maxChar: buildMaxCharRule(40),
      emptyValue: buildEmptyValueRule('Rollout help company name'),
    },
    companyAddressLine1: {
      maxChar: buildMaxCharRule(36),
      notApplicable: buildNotApplicableRule(),
      emptyValue: buildEmptyValueRule('Rollout help company address line 1'),
    },
    companyAddressLine2: {
      maxChar: buildMaxCharRule(36),
    },
    companyCity: {
      maxChar: buildMaxCharRule(22),
      emptyValue: buildEmptyValueRule('Rollout help company city'),
    },
    companyState: {
      maxChar: buildMaxCharRule(22),
      emptyValue: buildEmptyValueRule('Rollout help company state'),
    },
    companyZipCode: {
      maxChar: buildMaxCharRule(10),
      emptyValue: buildEmptyValueRule('Rollout help company zip code'),
    },
    companyPhone: {
      emptyValue: buildEmptyValueRule('Rollout help company phone'),
      phoneRegex: {
        re: /^(\+|\(|\d)[0-9a-zA-Z ,.()-]{2,35}$/,
        error:
          'Must start with +, (, or a number and must follow a standard formatting convention. E.g., +01 (555) 243-0321 ext. 44',
      },
    },
    companyWebsite: {
      maxChar: buildMaxCharRule(36),
    },
    companyEmail: {
      maxChar: buildMaxCharRule(50),
    },
  }
}
